/* eslint-disable react/jsx-no-useless-fragment */
import PropTypes from 'prop-types';
import { useState } from 'react';
// @mui
import {
  Box,
  Card,
  Stack,
  Avatar,
  Checkbox,
  Typography,
  CardHeader,
  FormControlLabel,
  MenuItem
} from '@mui/material';
import ReactPlayer from 'react-player';
// utils
import { useDispatch, useSelector } from 'react-redux';
import { TableMoreMenu } from '../../fl-components/table';
import { fDate } from '../../utils/formatTime';
// components
import Iconify from '../../fl-components/Iconify';
import ImageCarousel from './ImageCarousel';
import { deleteFeed, dislikeFeed, likeFeed } from '../../service/feed.service';
import { DELETE_ORG_FEED, DELETE_PERSONAL_FEED, UPDATE_FEED } from '../../redux/constants';
import PollOptions from './container/PollOptions';
import MCQContainer from './container/MCQContainer';
import LikeDetailsModal from './modal/LikeDetailsModal';
import Label from '../../fl-components/Label';

// ----------------------------------------------------------------------

FeedContainer.propTypes = {
  post: PropTypes.object
};

export default function FeedContainer({ post }) {
  const currentUser = useSelector((state) => state.auth.currentUser);
  const { createdBy } = post;
  const dispatch = useDispatch();

  const [isLiked, setLiked] = useState(post?.likesBy?.includes(currentUser.id) ?? false);

  const [likes, setLikes] = useState(post?.likesCount ?? 0);

  const [openMenu, setOpenMenuActions] = useState(null);

  const [displayLikesModal, setDisplayLikeModal] = useState(false);

  const handleOpenMenu = (event) => {
    setOpenMenuActions(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenuActions(null);
  };

  const handleDelete = async () => {
    await deleteFeed(post?.id ?? post?._id);
    dispatch({
      type: DELETE_PERSONAL_FEED,
      payload: {
        feedObj: post
      }
    });
    dispatch({
      type: DELETE_ORG_FEED,
      payload: {
        feedObj: post
      }
    });
  };

  const handleLike = async () => {
    setLiked(true);
    setLikes(likes + 1);
    const res = await likeFeed(post?.id ?? post?._id, currentUser.id);
    if (res) {
      dispatch({
        type: UPDATE_FEED,
        payload: {
          isUpdateOrg: true,
          feedObj: res
        }
      });
      setLikes(res.likesCount);
    } else {
      setLiked(false);
      setLikes((likes) => likes - 1);
    }
  };

  const handleUnlike = async () => {
    setLiked(false);
    setLikes(likes - 1);
    const res = await dislikeFeed(post?.id ?? post?._id, currentUser.id);
    if (res) {
      dispatch({
        type: UPDATE_FEED,
        payload: {
          isUpdateOrg: true,
          feedObj: res
        }
      });
      setLiked(false);
      setLikes(res.likesCount);
    } else {
      setLiked(true);
      setLikes((likes) => likes + 1);
    }
  };

  return (
    <Card>
      <CardHeader
        disableTypography
        avatar={
          <Avatar
            key={createdBy?.fullName}
            alt={createdBy?.fullName}
            src={createdBy?.profileImage}
          />
        }
        title={
          <Typography variant="subtitle2" color="text.primary">
            {createdBy?.fullName}
          </Typography>
        }
        subheader={
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            {fDate(post.createdAt)}
          </Typography>
        }
        action={
          <>
            <Label sx={{ textTransform: 'capitalize' }}>{post?.type}</Label>
            {(createdBy?.id ?? createdBy?._id) === currentUser.id && (
              <TableMoreMenu
                open={openMenu}
                onOpen={handleOpenMenu}
                onClose={handleCloseMenu}
                actions={
                  <MenuItem
                    onClick={() => {
                      handleDelete();
                      handleCloseMenu();
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <Iconify icon="eva:trash-2-outline" />
                    Delete
                  </MenuItem>
                }
              />
            )}
          </>
        }
      />

      <Stack spacing={3} sx={{ p: 3 }}>
        {post?.content && (
          <Typography sx={{ whiteSpace: 'pre-line' }}>
            {/* <div
              style={{ whiteSpace: 'pre-line' }}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: post.content }}
            /> */}
            {post.content}
          </Typography>
        )}

        {post?.type === 'poll' && <PollOptions post={post} />}
        {post?.type === 'mcq' && <MCQContainer post={post} />}

        {/* Display image here */}
        {post?.type === 'photo' && <ImageCarousel images={post.medias} />}
        {post?.type === 'video' && (
          <Box
            sx={{
              iframe: {
                borderRadius: '8px'
              }
            }}
          >
            <ReactPlayer
              url={post.medias[0].url}
              controls
              width="100%"
              height="100%"
              style={{
                borderRadius: '10px',
                height: '100%'
              }}
            />
          </Box>
        )}

        <Stack direction="row" alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                color="error"
                checked={isLiked}
                icon={<Iconify icon="eva:heart-fill" width={25} height={25} />}
                checkedIcon={<Iconify icon="eva:heart-fill" width={25} height={25} />}
                onChange={isLiked ? handleUnlike : handleLike}
              />
            }
            label={
              likes !== 0 ? (
                <Typography
                  fontSize={15.5}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDisplayLikeModal(true);
                  }}
                  sx={{
                    ':hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {likes}
                </Typography>
              ) : (
                <></>
              )
            }
            sx={{ minWidth: 72, mr: 0 }}
          />
          <Stack />
          <Box sx={{ flexGrow: 1 }} />
        </Stack>
      </Stack>

      {displayLikesModal && (
        <LikeDetailsModal
          isOpen={displayLikesModal}
          handleClose={() => setDisplayLikeModal(false)}
          postId={post?.id ?? post?._id}
        />
      )}
    </Card>
  );
}
